"use client"

import { useTranslations } from "next-intl"
import { useEffect, useMemo, useState, useRef, useCallback, useId } from "react"
import type React from "react"
import { cn, formatPrice, getFirstImageUrl } from "@/lib/utils"
import type { ChatMessage } from "@/hooks/use-realtime-chat"
import { createClient } from "@/utils/supabase/client"
import { ImageGalleryModal } from "@/components/chat/image-gallery-modal"
import { useHeicImage } from "@/hooks/use-heic-image"
import { CARD_SM, SQUARE_LG, imagePresetUrl, type ImagePreset } from "@/lib/images/presets"
import { User } from "lucide-react"
import Link from "next/link"
import { OrderMessageCard } from "@/components/chat/OrderMessageCard"
import { ChatListingDetailSheet } from "@/components/chat/ChatListingDetailSheet"
import { ChatGalleryOverlay } from "@/components/chat/ChatGalleryOverlay"
import { ChatBubble } from "@/components/chat/ChatBubble"
import { useListingSellerBanned } from "@/hooks/use-listing-seller-banned"
import { OfferBubble } from "@/components/offers/offer-bubble"
import { getCountryName, getStateName } from "@/lib/paypal-countries"
import { ChatReplyQuote } from "@/components/chat/ChatReplyQuote"
import { ChatReactionPills } from "@/components/chat/ChatReactionPills"
import type { ChatReplyPreview, ReplySenderContext } from "@/lib/chat/reply-preview"
import type { ChatMessageReactionCounts } from "@/lib/chat/message-reactions-ui"
import type { MessageAnchorRect } from "@/hooks/use-chat-message-interactions"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { chatBubbleTimestampClass, chatImageTimestampClass } from "@/lib/chat/chat-ui-tokens"

// ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: assemble a display string from the
// structured ship-from trio carried on the chat-message payload. Replaces
// the deleted `useListingLocation` hook (which previously re-fetched
// `listings.location` per-message).
function shipFromDisplay(
  shipFromAddress: { city: string | null; state: string | null; country: string | null } | null | undefined,
): string {
  if (!shipFromAddress) return ""
  const { city, state, country } = shipFromAddress
  const parts: string[] = []
  if (city) parts.push(city)
  if (state && country) {
    const stateName = getStateName(state, country)
    if (stateName) parts.push(stateName)
  } else if (state) {
    parts.push(state)
  }
  if (country) {
    const countryName = getCountryName(country)
    if (countryName) parts.push(countryName)
  }
  return parts.join(", ")
}

const LONG_PRESS_MS = 400

function ChatIncomingAvatarSlot({
  showAvatar,
  avatarUrl,
  displayName,
}: {
  showAvatar: boolean
  avatarUrl?: string | null
  displayName: string
}) {
  if (showAvatar) {
    return (
      <Avatar className="h-6 w-6 shrink-0 border border-border">
        <AvatarImage src={avatarUrl ?? undefined} alt={displayName} />
        <AvatarFallback className="bg-muted">
          <User className="h-3.5 w-3.5 text-muted-foreground" />
        </AvatarFallback>
      </Avatar>
    )
  }
  return <div className="h-6 w-6 shrink-0" aria-hidden />
}

function InlineBubbleTimestamp({ label, isOwnMessage }: { label: string; isOwnMessage: boolean }) {
  if (!label) return null
  return <span className={chatBubbleTimestampClass(isOwnMessage)}>{label}</span>
}

function useMessageLongPress(onLongPress: (() => void) | undefined, disabled?: boolean) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])
  const onPointerDown = useCallback(() => {
    if (disabled || !onLongPress) return
    clear()
    timerRef.current = setTimeout(onLongPress, LONG_PRESS_MS)
  }, [clear, disabled, onLongPress])
  useEffect(() => clear, [clear])
  return { onPointerDown, onPointerUp: clear, onPointerLeave: clear, onPointerCancel: clear }
}

const isUuid = (value: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)

interface ChatOfferPayload {
  type: "offer"
  id: string
  amount: number
  buyer_id: string
  seller_id: string
  listing_id: string
  expires_at: string
  message?: string
  status?: "pending" | "accepted" | "declined" | "counter_offered" | "expired" | "withdrawn"
  currency?: string
  listing_price?: number | null
}

function parseOfferPayload(raw: string | null | undefined): ChatOfferPayload | null {
  if (!raw || typeof raw !== "string") return null
  const trimmed = raw.trim()
  if (!trimmed.startsWith("{")) return null
  try {
    const parsed = JSON.parse(trimmed) as Partial<ChatOfferPayload>
    if (parsed?.type !== "offer") return null
    if (typeof parsed.id !== "string" || typeof parsed.listing_id !== "string") return null
    if (typeof parsed.buyer_id !== "string" || typeof parsed.seller_id !== "string") return null
    if (typeof parsed.amount !== "number" || !Number.isFinite(parsed.amount)) return null
    if (typeof parsed.expires_at !== "string") return null
    return parsed as ChatOfferPayload
  } catch {
    return null
  }
}

// ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: `useListingLocation` was deleted.
// Structured `shipFromAddress` is now carried directly on the chat-message
// payload (`/api/chat/messages`, `/api/chat/send`) — no per-message lookup.
// The on-demand legacy fetch also violated the frontend HARD GATE
// (supabase.from inside components/**); the new payload makes it unneeded.

function mapListingRow(data: Record<string, unknown>) {
  const profile = Array.isArray((data as any).profiles) ? (data as any).profiles[0] : (data as any).profiles
  // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: read structured trio off the
  // `listings_with_effective_published_at` view (ship_from_city/state/country
  // are resolved server-side: per-listing pin → seller's main fallback).
  const city = (data as any).ship_from_city ?? null
  const state = (data as any).ship_from_state ?? null
  const country = (data as any).ship_from_country ?? null
  const shipFromAddress =
    city == null && state == null && country == null
      ? null
      : { city, state, country }
  return {
    id: (data as any).id ?? null,
    user_id: (data as any).user_id ?? null,
    // Phase 4 (2026-06-29) of shippo-listing-discriminator: visibility enum replaces is_chat_private
    visibility: (data as any).visibility ?? null,
    chat_private_cancelled_at: (data as any).chat_private_cancelled_at ?? null,
    deleted_at: (data as any).deleted_at ?? null,
    title: (data as any).title ?? "Listing",
    description: (data as any).description ?? "",
    price: (data as any).price ?? null,
    currency: (data as any).currency ?? null,
    quantity: (data as any).quantity ?? null,
    image_urls: (data as any).image_urls ?? [],
    condition: (data as any).condition ?? "",
    grade: (data as any).grade ?? "",
    shipFromAddress,
    shipping_config: (data as any).shipping_config ?? null,
    shipping_mode: (data as any).shipping_mode ?? null,
    seller_username: profile?.username ?? null,
    seller_avatar_url: profile?.avatar_url ?? null,
    seller_checkout_note: typeof profile?.checkout_note === "string" ? profile.checkout_note : null,
  }
}

function useListingById(listingId?: string | null) {
  const [listing, setListing] = useState<any | null>(null)
  // The Supabase browser client is a singleton, so every message bubble that
  // references the same listing would otherwise open a channel with the SAME
  // topic. Supabase returns the already-subscribed channel object and the
  // second `.on()` throws "cannot add postgres_changes callbacks ... after
  // subscribe()". A per-instance suffix keeps each subscriber's topic unique.
  const instanceId = useId()
  useEffect(() => {
    if (!listingId) return
    let active = true
    const supabase = createClient()
    const load = async () => {
      try {
        // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: switched from `listings`
        // table to the `listings_with_effective_published_at` view so the
        // resolved ship-from trio (`ship_from_city/state/country`) is
        // projected directly — no client-side join.
        // Note: this `supabase.from` in `components/**` is grandfathered
        // legacy per CLAUDE.md frontend HARD GATE (kept for chat realtime
        // subscription; lift-and-shift to a hook is Phase 3 / later).
        const { data } = await supabase
          .from("listings_with_effective_published_at")
          .select(
            "id, user_id, visibility, chat_private_cancelled_at, deleted_at, title, description, price, currency, quantity, image_urls, condition, grade, ship_from_city, ship_from_state, ship_from_country, shipping_config, shipping_mode, profiles:user_id(username, avatar_url, checkout_note)",
          )
          .eq("id", listingId)
          .single()
        if (data && active) setListing(mapListingRow(data as Record<string, unknown>))
      } catch {}
    }
    void load()
    const channel = supabase
      .channel(`chat-message-listing:${listingId}:${instanceId}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "listings", filter: `id=eq.${listingId}` },
        () => {
          if (active) void load()
        },
      )
      .subscribe()
    return () => {
      active = false
      try {
        void supabase.removeChannel(channel)
      } catch {}
    }
  }, [listingId, instanceId])
  return listing
}

/**
 * A chat image bubble, sized by the caller.
 *
 * `preset` is REQUIRED and has no default on purpose. This component renders
 * into three different boxes — a `w-48 aspect-3/4` listing card in the stacked
 * multi-listing bubble, a `w-40 aspect-3/4` photo bubble, and a ~78 px square
 * cell in the 2x2 photo grid — so there is no single right answer, and until now
 * every one of them was served the **untouched original upload** (`displaySrc ||
 * src` straight into `<img>`). That is why the step-7 sweep could not fix this
 * with a literal swap: one call site's box is a quarter the area of another's.
 *
 * HEIC ORDERING — the predicate must see a RAW ref and the preset must be
 * applied to the funnel's OUTPUT, in that order. `useHeicImage(src)` gets the
 * untouched `src` so `isHeicUrl` reads a real path; the resize then wraps
 * `displaySrc`, which is either the resolved public URL (rewritten) or a `blob:`
 * from the conversion (passed through untouched — the bytes are already decoded
 * in memory and there is nothing for the CDN to resize). Sizing `src` first and
 * converting after would hand imgproxy a HEIC it cannot decode.
 *
 * @see lib/images/presets.ts
 * @see lib/image-url-helper.ts `isHeicUrl` — why the ordering is load-bearing
 */
function ImageWithPending({ src, alt, preset, pending, onLoaded, onImageClick, compact = false }: { src: string; alt: string; preset: ImagePreset; pending: boolean; onLoaded?: () => void; onImageClick?: () => void; compact?: boolean }) {
  const [loaded, setLoaded] = useState(false)
  const showSkeleton = pending || !loaded
  const { displaySrc, isConverting } = useHeicImage(src)
  const sizedSrc = imagePresetUrl(displaySrc || src, preset)

  if (compact) {
    return (
      <div className="absolute inset-0 w-full h-full">
        {(showSkeleton || isConverting) && (
          <>
            <div className="absolute inset-0 animate-pulse bg-muted" />
            <div className="absolute inset-0 grid place-items-center">
              <div className="h-6 w-6 rounded-full border-2 border-muted-foreground/30 border-t-transparent animate-spin" />
            </div>
          </>
        )}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={sizedSrc}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-cover ${(showSkeleton || isConverting) ? "opacity-0" : "opacity-100"} transition-opacity duration-300`}
          onLoad={() => { setLoaded(true); onLoaded?.() }}
          onError={(e) => { ;(e.target as HTMLImageElement).style.display = "none" }}
        />
      </div>
    )
  }

  return (
    <div className="mb-0">
      <div
        className="relative inline-block w-40 aspect-3/4 rounded-3xl overflow-hidden cursor-pointer hover:opacity-90 transition-opacity active:scale-[0.98]"
        onClick={onImageClick}
      >
        {(showSkeleton || isConverting) && (
          <>
            <div className="absolute inset-0 animate-pulse bg-linear-to-r from-black/20 via-black/10 to-black/20" />
            <div className="absolute inset-0 grid place-items-center">
              <div className="h-6 w-6 rounded-full border-2 border-white/70 border-t-transparent animate-spin" />
            </div>
          </>
        )}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={sizedSrc}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-cover ${(showSkeleton || isConverting) ? "opacity-0" : "opacity-100"} transition-opacity duration-300`}
          onLoad={() => { setLoaded(true); onLoaded?.() }}
          onError={(e) => { ;(e.target as HTMLImageElement).style.display = "none" }}
        />
      </div>
    </div>
  )
}

export function ChatMessageItem({
  message,
  isOwnMessage,
  showHeader,
  showAvatar: showAvatarProp,
  displayName,
  avatarUrl,
  isTimestampVisible,
  bubbleClassNames,
  timestampVariant = "side",
  timestampFormat,
  roomName,
  currentUserId,
  otherParticipantId,
  showTail = true,
  onOrderCardClick,
  imageGroup,
  listingGroup,
  counterpartyBanned = false,
  replyPreview = null,
  reactions,
  viewerReaction,
  onReactionToggle,
  onOpenMessageMenu,
  replySenderContext,
  overlayMode = false,
  hideForOverlay = false,
}: {
  message: ChatMessage
  isOwnMessage?: boolean
  showHeader?: boolean
  displayName: string
  avatarUrl?: string | null
  isTimestampVisible?: boolean
  bubbleClassNames?: {
    own?: string
    other?: string
  }
  timestampVariant?: "side" | "inline"
  timestampFormat?: (date: Date) => string
  showAvatar?: boolean
  roomName?: string
  currentUserId?: string | null
  otherParticipantId?: string | null
  showTail?: boolean
  onOrderCardClick?: (href: string) => void
  imageGroup?: ChatMessage[]
  listingGroup?: ChatMessage[]
  counterpartyBanned?: boolean
  replyPreview?: ChatReplyPreview | null
  reactions?: ChatMessageReactionCounts
  viewerReaction?: string | null
  onReactionToggle?: (emoji: string) => void
  onOpenMessageMenu?: (anchor: MessageAnchorRect) => void
  replySenderContext?: ReplySenderContext
  overlayMode?: boolean
  hideForOverlay?: boolean
}) {
  const [galleryState, setGalleryState] = useState<{ images: { id: string; url: string; alt: string }[]; initialIndex: number } | null>(null)
  const tChat = useTranslations("chat")
  const [listingSheetOpen, setListingSheetOpen] = useState(false)
  const [listingOverlayOpen, setListingOverlayOpen] = useState(false)
  const [sheetListing, setSheetListing] = useState<any | null>(null)
  const [galleryOverlayOpen, setGalleryOverlayOpen] = useState(false)
  const [stableIsOwnMessage] = useState(isOwnMessage)
  const messageShellRef = useRef<HTMLDivElement>(null)

  const requestMessageMenu = useCallback(() => {
    if (overlayMode || !onOpenMessageMenu || !messageShellRef.current) return
    const rect = messageShellRef.current.getBoundingClientRect()
    onOpenMessageMenu({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
    })
  }, [onOpenMessageMenu, overlayMode])

  const longPressHandlers = useMessageLongPress(
    onOpenMessageMenu ? requestMessageMenu : undefined,
    overlayMode || !onOpenMessageMenu,
  )

  useEffect(() => {
    if (stableIsOwnMessage !== isOwnMessage) {
      console.warn('[CHAT MESSAGE ALIGNMENT CHANGE]', {
        messageId: message.id,
        stable: stableIsOwnMessage,
        current: isOwnMessage,
        senderId: message.senderId
      })
    }
  }, [isOwnMessage, message.id, message.senderId, stableIsOwnMessage])
  const timestampLabel = useMemo(() => {
    const date = new Date(message.createdAt)
    if (Number.isNaN(date.getTime())) {
      return ""
    }
    if (timestampFormat) {
      return timestampFormat(date)
    }
    return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
  }, [message.createdAt, timestampFormat])
  const ownBubbleClass = bubbleClassNames?.own ?? "bg-primary text-primary-foreground"
  const otherBubbleClass = bubbleClassNames?.other ?? "bg-muted text-foreground"
  const attachmentOffset = ""
  const alignmentClass = useMemo(() => {
    const alignment = stableIsOwnMessage ? "justify-end flex-row" : "justify-start flex-row"
    return alignment
  }, [stableIsOwnMessage])
  
  const hasAttachment = Boolean(
    (message.listing || message.imageUrl) ||
    (message.content && (message.content.trim().toLowerCase() === "[order]" || message.content.trim().toLowerCase() === "order")) ||
    message.order
  )
  
  const AttachmentLine = () => (
    <div
      className="w-[4px] sm:w-[5px] rounded-full bg-border/80 self-stretch min-h-[40px]"
      aria-hidden
      style={{ 
        minWidth: '4px',
        flexShrink: 0,
        willChange: 'transform'
      }}
    />
  )
  const incomingAvatarVisible = Boolean(showAvatarProp ?? showHeader)
  
  const handleImageClick = (imageSrc: string, allUrls?: string[], index?: number) => {
    const urls = allUrls && allUrls.length > 0 ? allUrls : [imageSrc]
    const idx = typeof index === "number" && index >= 0 ? index : urls.indexOf(imageSrc)
    setGalleryState({
      images: urls.map((url, i) => ({ id: `${message.id}-img-${i}`, url, alt: `Chat image ${i + 1}` })),
      initialIndex: idx >= 0 ? idx : 0,
    })
  }

  const handleCloseZoom = () => {
    setGalleryState(null)
  }
  const normalizedContent = (message.content || "").trim().toLowerCase()
  const fallbackListingId =
    (message as any).listingId ||
    (message as any).listing_id ||
    message.attachmentId ||
    message.id ||
    `listing-${message.createdAt}`
  const fetchedListing = useListingById((message as any).listingId || (message as any).listing_id || undefined)
  const baseListingSource = useMemo(() => {
    const fromMsg = message.listing as Record<string, unknown> | undefined | null
    if (!fromMsg && !fetchedListing) return null
    if (!fromMsg) return fetchedListing
    if (!fetchedListing) return fromMsg
    const a = String((fromMsg as any).id ?? "")
    const b = String((fetchedListing as any).id ?? "")
    if (a && b && a !== b) return fromMsg
    return { ...fromMsg, ...fetchedListing }
  }, [message.listing, fetchedListing])
  const baseListing = baseListingSource ?? ((message as any).listingId || (message as any).listing_id ? {
    id: (message as any).listingId || (message as any).listing_id,
    visibility: null,
    chat_private_cancelled_at: null,
    deleted_at: null,
    title: (message as any).content && (message as any).content !== '[Listing]' ? (message as any).content : "Listing",
    price: null,
    image_urls: [],
    condition: "",
    grade: "",
    location: null,
    seller_username: message.user?.name ?? null,
    seller_avatar_url: message.user?.avatar_url ?? null,
  } : (normalizedContent === "[listing]" ? {
    id: fallbackListingId,
    visibility: null,
    chat_private_cancelled_at: null,
    deleted_at: null,
    title: "Listing",
    price: null,
    image_urls: [],
    condition: "",
    grade: "",
    location: null,
    seller_username: message.user?.name ?? null,
    seller_avatar_url: message.user?.avatar_url ?? null,
  } : null))
  const listingData = baseListing ?? (normalizedContent === "[listing]" ? {
    id: fallbackListingId,
    visibility: null,
    chat_private_cancelled_at: null,
    deleted_at: null,
    title: "Listing",
    price: null,
    image_urls: [],
    condition: "",
    grade: "",
    location: null,
    seller_username: message.user?.name ?? null,
    seller_avatar_url: message.user?.avatar_url ?? null,
  } : null)
  useEffect(() => {
    if (!listingData) return
    console.log("[CHAT CLIENT] render listing", {
      messageId: message.id,
      listingId: listingData.id,
      content: message.content,
    })
  }, [listingData, message.content, message.id])
  useEffect(() => {
    if (!listingData) return
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail as { messageId?: string; roomName?: string } | undefined
      if (d?.messageId === message.id && (d?.roomName ?? "") === (roomName ?? "")) setListingSheetOpen(true)
    }
    window.addEventListener("chat-checkout-back", handler as EventListener)
    return () => window.removeEventListener("chat-checkout-back", handler as EventListener)
  }, [listingData, message.id, roomName])

  const sheetListingCandidate = (sheetListing ?? listingData) as any

  const primaryListingIdForBan =
    listingData?.id && isUuid(String(listingData.id)) ? String(listingData.id) : undefined
  const listingSellerBanned = useListingSellerBanned(primaryListingIdForBan, currentUserId)

  const detailSheetListing = useMemo(() => {
    if (!listingData) return undefined
    const base = (sheetListing ?? listingData) as any
    // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: derive display location from
    // the structured `shipFromAddress` trio carried on the chat-message
    // payload (resolved server-side: per-listing pin → seller main).
    // Legacy fallback to `base.location` covers any pending in-flight
    // payloads that haven't been re-fetched yet.
    const loc =
      shipFromDisplay(base?.shipFromAddress) ||
      String(base?.location ?? "").trim() ||
      ""
    return { ...base, location: loc }
  }, [listingData, sheetListing])

  const contentIsOrderToken = normalizedContent === "[order]" || normalizedContent === "order"
  const derivedOrder = message.order ?? (contentIsOrderToken
    ? {
        orderNumber: "Order",
        orderId: undefined,
        total: 0,
        status: "pending",
        date: new Date().toISOString(),
        itemCount: 1,
      }
    : null)
  const hasOrder = Boolean(derivedOrder)
  const orderHref = derivedOrder?.orderNumber
    ? `/orders/${derivedOrder.orderNumber}`
    : derivedOrder?.orderId
    ? `/orders/${derivedOrder.orderId}`
    : null
  const showOrderText = hasOrder && message.content && !contentIsOrderToken

  const attachmentBelongsToCurrentUser = useMemo(() => {
    if (!hasAttachment || !currentUserId) return false
    const listingSellerId = (listingData as any)?.user_id ?? null
    const orderBuyerId = (message.order as any)?.userId ?? (message.order as any)?.user_id ?? null
    const orderSellerId = (message.order as any)?.seller_id ?? null
    return (
      (listingData && listingSellerId === currentUserId) ||
      (derivedOrder && (orderBuyerId === currentUserId || orderSellerId === currentUserId)) ||
      (!!message.imageUrl && !listingData && !hasOrder && !!stableIsOwnMessage)
    )
  }, [hasAttachment, currentUserId, listingData, message.order, derivedOrder, hasOrder, message.imageUrl, stableIsOwnMessage])

  const showAttachmentLine =
    hasAttachment &&
    !message.imageUrl &&
    ((stableIsOwnMessage && !attachmentBelongsToCurrentUser) ||
      (!stableIsOwnMessage && attachmentBelongsToCurrentUser))

  if (listingData) {
    const listingSources = listingGroup && listingGroup.length > 0 ? listingGroup : [message]
    const listings = listingSources.map((m) => (m as any).listing).filter(Boolean) as any[]
    const isMultiListing = (listingGroup && listingGroup.length > 1) || listings.length > 1
    const listingCount = listingGroup && listingGroup.length > 1 ? listingGroup.length : listings.length
    const activeListing = (detailSheetListing ?? sheetListingCandidate ?? listingData) as any
    const hasOrderForListing = Boolean(message.order && ((message.order as any)?.orderId || (message.order as any)?.orderNumber || (message.order as any)?.sellerOrderId))
    const orderBuyerId = (message.order as any)?.userId ?? (message.order as any)?.user_id ?? null
    const isCurrentUserBuyer = Boolean(currentUserId && orderBuyerId && currentUserId === orderBuyerId)
    const isOtherParticipantBuyer = Boolean(otherParticipantId && orderBuyerId && otherParticipantId === orderBuyerId)
    const listingSellerId = listingData?.user_id ?? null
    const isCurrentUserSeller = Boolean(currentUserId && listingSellerId && currentUserId === listingSellerId)
    const listingCancelled = Boolean(listingData?.chat_private_cancelled_at)
    const listingDeleted = Boolean(listingData?.deleted_at)
    const qty = listingData?.quantity
    const qtyKnown = qty != null && Number.isFinite(Number(qty))
    const outOfStock = qtyKnown && Number(qty) <= 0
    const listingSold = hasOrderForListing || (outOfStock && !listingCancelled)
    const listingGone = listingDeleted && !hasOrderForListing
    const listingUnavailable = !qtyKnown && !hasOrderForListing && !listingCancelled && !listingDeleted
    const showPurchased = hasOrderForListing && isCurrentUserBuyer
    const showSold = hasOrderForListing && isCurrentUserSeller && isOtherParticipantBuyer
    const showGoneFromOrder = hasOrderForListing && !showPurchased && !showSold
    const listingHardInactive = listingSold || listingCancelled || listingGone
    const listingInactive = listingHardInactive || listingUnavailable
    const listingIsFromOtherSeller = Boolean(
      listingSellerId && currentUserId && listingSellerId !== currentUserId,
    )
    const counterpartyBanAffectsListing = Boolean(counterpartyBanned && listingIsFromOtherSeller)
    const listingSellerBannedEffective = Boolean(listingSellerBanned || counterpartyBanAffectsListing)
    const listingBanBlocksInteraction = listingSellerBannedEffective && !listingHardInactive
    const showSellerBannedOverlay = listingBanBlocksInteraction
    const listingStatusLabel = listingCancelled
      ? tChat("cancelled")
      : listingGone
        ? tChat("gone")
        : hasOrderForListing
          ? showPurchased
            ? tChat("purchased")
            : showSold
              ? tChat("sold")
              : tChat("gone")
          : listingUnavailable
            ? tChat("unavailable")
            : tChat("sold")
    const listingItems = listings
      .map((l, idx) => {
        const cancelled = Boolean(l?.chat_private_cancelled_at)
        const deleted = Boolean(l?.deleted_at)
        const q = l?.quantity
        const qKnown = q != null && Number.isFinite(Number(q))
        const outOfStock = qKnown && Number(q) <= 0
        const singleWithOrder = listings.length === 1 && hasOrderForListing && !cancelled && !deleted
        const purchased = singleWithOrder && isCurrentUserBuyer
        const isListingSeller = Boolean(currentUserId && l?.user_id && currentUserId === l.user_id)
        const sold =
          (singleWithOrder && isListingSeller && isOtherParticipantBuyer) ||
          (listings.length > 1 && outOfStock && !cancelled)
        const gone = (deleted && !purchased) || (singleWithOrder && !purchased && !sold)
        const unavailable = !qKnown && !cancelled && !deleted && !purchased
        const status: "cancelled" | "sold" | "gone" | "purchased" | "unavailable" | null = cancelled
          ? "cancelled"
          : gone
            ? "gone"
            : purchased
              ? "purchased"
              : unavailable
                ? "unavailable"
                : sold
                  ? "sold"
                  : null
        return {
          id: String(l?.id ?? `${message.id}-lst-${idx}`),
          url: getFirstImageUrl(l?.image_urls) || "/placeholder.svg",
          type: "listing" as const,
          title: String(l?.title ?? "Listing"),
          price: l?.price != null ? formatPrice(Number(l.price), l.currency) : undefined,
          status,
          _listing: l,
        }
      })
      .filter((i) => Boolean(i.url))

    return (
      <div className={cn("flex w-full items-end gap-2", alignmentClass, attachmentOffset)}>
        {!stableIsOwnMessage ? (
          <ChatIncomingAvatarSlot
            showAvatar={incomingAvatarVisible}
            avatarUrl={avatarUrl}
            displayName={displayName}
          />
        ) : null}
        <div className={cn("flex flex-col", message.pending ? "opacity-70" : undefined)}>
          <div className="flex items-stretch" style={{ gap: '6px' }}>
            {!stableIsOwnMessage && showAttachmentLine && <AttachmentLine />}
            {isMultiListing ? (
              <div>
                <p className={cn("text-[13px] font-medium mb-1.5 flex items-center gap-1.5", stableIsOwnMessage ? "text-primary-foreground/80 dark:text-white/80 justify-end" : "text-muted-foreground dark:text-neutral-400")}>
                  <span className={cn("inline-grid grid-cols-2 gap-px w-3 h-3", stableIsOwnMessage ? "[&>span]:bg-primary-foreground/70 dark:[&>span]:bg-white/70" : "[&>span]:bg-muted-foreground dark:[&>span]:bg-neutral-400")}>
                    <span className="rounded-[1px]" />
                    <span className="rounded-[1px]" />
                    <span className="rounded-[1px]" />
                    <span className="rounded-[1px]" />
                  </span>
                  {listingCount} {tChat("listing")}
                </p>
                <button
                  type="button"
                  onClick={() => setListingOverlayOpen(true)}
                  disabled={!!message.pending || listingBanBlocksInteraction}
                  className="relative w-48 aspect-3/4 shrink-0 active:scale-[0.98] transition-transform"
                >
                <div className="absolute inset-0 z-0">
                  {listingItems.slice(0, 3).map((item, idx) => {
                    const scale = 1 - idx * 0.06
                    const translateY = idx * 10
                    const translateX = idx * 4
                    const rotate = (idx - 1) * 3
                    return (
                      <div
                        key={`${item.id}-${idx}`}
                        className="absolute inset-0"
                        style={{
                          transform: `translateX(${translateX}px) translateY(${translateY}px) rotate(${rotate}deg) scale(${scale})`,
                          transformOrigin: "center",
                          zIndex: 3 - idx,
                        }}
                      >
                        <div className="relative w-full h-full rounded-3xl overflow-hidden border border-border bg-muted shadow-lg">
                          <ImageWithPending
                            src={item.url}
                            alt={item.title || `Listing ${idx + 1}`}
                            // `w-48 aspect-3/4` (the stack's parent button) ->
                            // 384 device px at 2x DPR, CARD_SM exactly — the
                            // same rung, and therefore the same URL, as the
                            // single-listing chat card further down this file.
                            preset={CARD_SM}
                            pending={!!message.pending}
                            compact
                          />
                          {idx === 0 && item.price ? (
                            <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded-lg">
                              <p className="text-[12px] text-white font-bold">{item.price}</p>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div className="absolute bottom-2 left-2 z-5 bg-black/55 backdrop-blur-sm px-2 py-0.5 rounded-lg">
                  <p className="text-[12px] text-white font-semibold">{listingCount} {tChat("listing")}</p>
                </div>
                {listingBanBlocksInteraction ? (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 rounded-3xl">
                    <span className="text-[11px] font-semibold text-white bg-black/70 px-2.5 py-1 rounded-full">
                      {tChat("userBanned").toUpperCase()}
                    </span>
                  </div>
                ) : null}
                {timestampLabel ? (
                  <div className="pointer-events-none absolute bottom-2 right-2 z-20">
                    <span className={chatImageTimestampClass()}>{timestampLabel}</span>
                  </div>
                ) : null}
              </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setSheetListing(listingData)
                  setListingSheetOpen(true)
                }}
                disabled={!!message.pending || listingBanBlocksInteraction}
                className={cn(
                  "relative w-48 aspect-3/4 rounded-3xl overflow-hidden bg-muted border border-border shrink-0 transition-all",
                  listingBanBlocksInteraction
                    ? "cursor-not-allowed opacity-90"
                    : "cursor-pointer hover:opacity-90 active:scale-[0.98]",
                )}
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  // `w-48 aspect-3/4` `object-cover` -> 384 device px at 2x DPR,
                  // CARD_SM exactly. The old pair was 384/70 — one character off
                  // the preset's quality, i.e. a whole second cached copy of
                  // every listing photo for no visible difference.
                  src={imagePresetUrl(getFirstImageUrl(listingData.image_urls) || "/placeholder.svg", CARD_SM)}
                  alt={listingData.title || "Listing"}
                  className="absolute inset-0 z-0 w-full h-full object-cover"
                />
                {listingData.price != null && !showSellerBannedOverlay ? (
                  <div className="absolute bottom-2 left-2 z-5 bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded-lg">
                    <p className="text-[12px] text-white font-bold">
                      {formatPrice(Number(listingData.price), listingData.currency)}
                    </p>
                  </div>
                ) : null}
                {listingHardInactive ? (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 rounded-3xl">
                    <span className="text-[11px] font-semibold text-white bg-black/70 px-2.5 py-1 rounded-full">
                      {listingStatusLabel}
                    </span>
                  </div>
                ) : showSellerBannedOverlay ? (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 rounded-3xl">
                    <span className="text-[11px] font-semibold text-white bg-black/70 px-2.5 py-1 rounded-full">
                      {tChat("userBanned").toUpperCase()}
                    </span>
                  </div>
                ) : listingUnavailable ? (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/40 rounded-3xl">
                    <span className="text-[11px] font-semibold text-white bg-black/70 px-2.5 py-1 rounded-full">
                      {tChat("unavailable").toUpperCase()}
                    </span>
                  </div>
                ) : null}
                {timestampLabel ? (
                  <div className="pointer-events-none absolute bottom-2 right-2 z-20">
                    <span className={chatImageTimestampClass()}>{timestampLabel}</span>
                  </div>
                ) : null}
              </button>
            )}
            {stableIsOwnMessage && showAttachmentLine && <AttachmentLine />}
          </div>
        </div>

        <ChatListingDetailSheet
          isOpen={listingSheetOpen}
          onOpenChange={setListingSheetOpen}
          listing={detailSheetListing}
          messageId={message.id}
          roomName={roomName || ""}
          currentUserId={currentUserId}
          otherParticipantId={otherParticipantId}
          isOwnMessage={Boolean(stableIsOwnMessage)}
          senderId={message.senderId ?? null}
          order={message.order}
          disabled={!!message.pending || listingBanBlocksInteraction}
          counterpartySellerBanned={counterpartyBanAffectsListing}
        />

        <ChatGalleryOverlay
          items={listingItems.map(({ _listing, ...rest }) => rest)}
          isOpen={listingOverlayOpen}
          onClose={() => setListingOverlayOpen(false)}
          onPhotoClick={() => {}}
          onListingClick={(item) => {
            if (listingBanBlocksInteraction) return
            const next = (listingItems.find((x) => x.id === item.id) as any) || null
            if (next?._listing) {
              setSheetListing(next._listing)
              setListingSheetOpen(true)
            }
          }}
        />
      </div>
    )
  }

  const offerPayload = parseOfferPayload(message.content)
  if (offerPayload) {
    return (
      <div className={cn("flex w-full items-end gap-2", alignmentClass, attachmentOffset)}>
        {!stableIsOwnMessage ? (
          <ChatIncomingAvatarSlot
            showAvatar={incomingAvatarVisible}
            avatarUrl={avatarUrl}
            displayName={displayName}
          />
        ) : null}
        <OfferBubble
          payload={offerPayload}
          currentUserId={currentUserId ?? null}
          ownMessage={stableIsOwnMessage}
        />
      </div>
    )
  }

  if (hasOrder) {
    return (
      <div className={cn("flex w-full items-end gap-2", alignmentClass, attachmentOffset)}>
        {!stableIsOwnMessage ? (
          <ChatIncomingAvatarSlot
            showAvatar={incomingAvatarVisible}
            avatarUrl={avatarUrl}
            displayName={displayName}
          />
        ) : null}
        <div className={cn("flex flex-col max-w-[80%] min-w-0 shrink-0 gap-2", message.pending ? "opacity-70" : undefined)}>
          <div className="flex items-stretch" style={{ gap: '6px' }}>
            {!stableIsOwnMessage && showAttachmentLine && <AttachmentLine />}
            <div
              className="relative rounded-2xl border border-border bg-muted/30 px-3 py-2"
              style={{ contain: 'layout style paint' }}
            >
              {orderHref ? (
                onOrderCardClick ? (
                  <span
                    role="link"
                    tabIndex={0}
                    className="block cursor-pointer"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onOrderCardClick(orderHref);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOrderCardClick(orderHref);
                      }
                    }}
                  >
                    <OrderMessageCard 
                      order={derivedOrder!} 
                      variant="bubble" 
                      isPending={message.pending}
                    />
                  </span>
                ) : (
                  <Link href={orderHref} onClick={(e) => e.stopPropagation()} className="block">
                    <OrderMessageCard 
                      order={derivedOrder!} 
                      variant="bubble" 
                      isPending={message.pending}
                    />
                  </Link>
                )
              ) : (
                <OrderMessageCard 
                  order={derivedOrder!} 
                  variant="bubble" 
                  isPending={message.pending}
                />
              )}
              {timestampLabel ? (
                <div className="pointer-events-none absolute bottom-2 right-2 z-20">
                  <span className={chatImageTimestampClass()}>{timestampLabel}</span>
                </div>
              ) : null}
            </div>
            {stableIsOwnMessage && showAttachmentLine && <AttachmentLine />}
          </div>

          {showOrderText && (
            <ChatBubble
              isOwnMessage={!!stableIsOwnMessage}
              className="max-w-[90vw]"
              showTail={showTail}
              isPending={!!message.pending}
            >
              <div className="flex flex-wrap items-end gap-x-1.5 self-stretch">
                <p className="chat-bubble-text min-w-0 flex-1 text-[15px] leading-relaxed">{message.content}</p>
                <InlineBubbleTimestamp label={timestampLabel} isOwnMessage={!!stableIsOwnMessage} />
              </div>
            </ChatBubble>
          )}
        </div>

      </div>
    )
  }
  
  // Affichage pour messages texte OU image
  if (!listingData && normalizedContent === "[listing]") {
    return (
      <div className={cn("flex w-full items-end gap-2", alignmentClass, attachmentOffset)}>
        {!stableIsOwnMessage ? (
          <ChatIncomingAvatarSlot
            showAvatar={incomingAvatarVisible}
            avatarUrl={avatarUrl}
            displayName={displayName}
          />
        ) : null}
        <div className={cn("relative max-w-[80%] rounded-2xl px-3 py-2 border border-border bg-muted/30", message.pending ? "opacity-70" : undefined)}>
          <p className="text-sm whitespace-pre-wrap break-normal">Listing envoyé</p>
        </div>
      </div>
    )
  }
  const imageUrlsForDisplay = (imageGroup?.map((m) => m.imageUrl).filter(Boolean) as string[]) ?? (message.imageUrl ? [message.imageUrl] : [])
  const hasImages = imageUrlsForDisplay.length > 0
  const isImageOnly = hasImages && (!message.content || message.content === "[Image]" || message.content === "[Images]")
  const isMultiImage = (imageGroup && imageGroup.length > 1) || imageUrlsForDisplay.length > 1
  const imageCount = imageGroup && imageGroup.length > 1 ? imageGroup.length : imageUrlsForDisplay.length
  return (
    <>
    <div className={cn("flex w-full items-end gap-2", alignmentClass, attachmentOffset, hideForOverlay && "pointer-events-none opacity-0")}>      
      {!stableIsOwnMessage ? (
        <ChatIncomingAvatarSlot
          showAvatar={incomingAvatarVisible}
          avatarUrl={avatarUrl}
          displayName={displayName}
        />
      ) : null}

      {isImageOnly ? (
        <div
          ref={messageShellRef}
          className={cn("flex flex-col max-w-[80%] select-none [webkit-touch-callout:none]", stableIsOwnMessage ? "items-end" : "items-start")}
          onContextMenu={(e) => {
            if (overlayMode || !onOpenMessageMenu) return
            e.preventDefault()
            requestMessageMenu()
          }}
          {...longPressHandlers}
        >
          {showHeader && (
            <div className="text-[10px] opacity-70 mb-1 px-1">{displayName}</div>
          )}
          <div className="flex items-stretch" style={{ gap: '6px' }}>
            {!stableIsOwnMessage && showAttachmentLine && <AttachmentLine />}
            <div className="relative">
              {isMultiImage ? (
                <div>
                  <p className={cn("text-[13px] font-medium mb-1.5 flex items-center gap-1.5", stableIsOwnMessage ? "text-primary-foreground/80 dark:text-white/80 justify-end" : "text-muted-foreground dark:text-neutral-400")}>
                    <span className={cn("inline-grid grid-cols-2 gap-px w-3 h-3", stableIsOwnMessage ? "[&>span]:bg-primary-foreground/70 dark:[&>span]:bg-white/70" : "[&>span]:bg-muted-foreground dark:[&>span]:bg-neutral-400")}>
                      <span className="rounded-[1px]" />
                      <span className="rounded-[1px]" />
                      <span className="rounded-[1px]" />
                      <span className="rounded-[1px]" />
                    </span>
                    {imageCount} Photos
                  </p>
                  <button
                    type="button"
                    onClick={() => setGalleryOverlayOpen(true)}
                    className="relative w-40 aspect-3/4 shrink-0 active:scale-[0.98] transition-transform"
                  >
                  <div className="absolute inset-0">
                    {imageUrlsForDisplay.slice(0, 3).map((url, idx) => {
                      const scale = 1 - idx * 0.06
                      const translateY = idx * 10
                      const translateX = idx * 4
                      const rotate = (idx - 1) * 3
                      return (
                        <div
                          key={`${url}-${idx}`}
                          className="absolute inset-0"
                          style={{
                            transform: `translateX(${translateX}px) translateY(${translateY}px) rotate(${rotate}deg) scale(${scale})`,
                            transformOrigin: "center",
                            zIndex: 3 - idx,
                          }}
                        >
                          <div className="relative w-full h-full rounded-3xl overflow-hidden border border-border bg-muted shadow-lg">
                            <ImageWithPending
                              src={url}
                              alt={`Photo ${idx + 1}`}
                              // `w-40 aspect-3/4` stack -> 320 device px at 2x,
                              // rounds UP to CARD_SM (384).
                              preset={CARD_SM}
                              pending={!!message.pending}
                              compact
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <div className="absolute bottom-2 left-2 bg-black/55 backdrop-blur-sm px-2 py-0.5 rounded-lg">
                    <p className="text-[12px] text-white font-semibold">{imageCount} Photos</p>
                  </div>
                  {timestampLabel ? (
                    <div className="pointer-events-none absolute bottom-2 right-2 z-20">
                      <span className={chatImageTimestampClass()}>{timestampLabel}</span>
                    </div>
                  ) : null}
                </button>
                </div>
              ) : (
                <div className="relative inline-block" onClick={() => handleImageClick(imageUrlsForDisplay[0]!)}>
                  <ImageWithPending
                    src={imageUrlsForDisplay[0]!}
                    alt="Shared image"
                    // The non-compact bubble's own box: `w-40 aspect-3/4` ->
                    // 320 device px at 2x, rounds UP to CARD_SM (384). Tapping
                    // opens the full-size viewer, which is a separate request.
                    preset={CARD_SM}
                    pending={!!message.pending}
                    onImageClick={() => handleImageClick(imageUrlsForDisplay[0]!)}
                  />
                  {timestampLabel ? (
                    <div className="pointer-events-none absolute bottom-2 right-2 z-20">
                      <span className={chatImageTimestampClass()}>{timestampLabel}</span>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            {stableIsOwnMessage && showAttachmentLine && <AttachmentLine />}
          </div>
        </div>
      ) : (
        <div
          ref={messageShellRef}
          className={cn("flex shrink-0 max-w-[75vw] sm:max-w-[min(75vw,28rem)] flex-col select-none [webkit-touch-callout:none]", stableIsOwnMessage ? "items-end" : "items-start")}
          onContextMenu={(e) => {
            if (overlayMode || !onOpenMessageMenu) return
            e.preventDefault()
            requestMessageMenu()
          }}
          {...longPressHandlers}
        >
          <ChatBubble
            isOwnMessage={!!stableIsOwnMessage}
            showTail={showTail}
            isPending={!!message.pending}
          >
            {replyPreview ? (
              <ChatReplyQuote
                replyTo={replyPreview}
                isOwnMessage={!!stableIsOwnMessage}
                fallbackSenderName={displayName}
                senderContext={replySenderContext}
              />
            ) : null}
            {hasImages && (
              <div className="relative inline-block">
                <div onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
                  {imageUrlsForDisplay.length === 1 ? (
                    <ImageWithPending
                      src={imageUrlsForDisplay[0]!}
                      alt="Shared image"
                      // Same `w-40 aspect-3/4` bubble as above -> CARD_SM.
                      preset={CARD_SM}
                      pending={!!message.pending}
                      onImageClick={() => handleImageClick(imageUrlsForDisplay[0]!)}
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-1 w-40 rounded-2xl overflow-hidden">
                      {imageUrlsForDisplay.slice(0, 4).map((url, idx) => (
                        <button
                          key={`${url}-${idx}`}
                          type="button"
                          className="relative aspect-square bg-muted overflow-hidden"
                          onClick={() => handleImageClick(url, imageUrlsForDisplay, idx)}
                        >
                          {/* 2x2 grid inside `w-40` with `gap-1` -> a ~78 px
                              SQUARE cell, i.e. ~156 device px at 2x. SQUARE_LG
                              (256 square, `cover`) is the round-UP and crops
                              server-side, so this is the one call site here
                              whose rung is not CARD_SM — the reason `preset`
                              has no default. */}
                          <ImageWithPending src={url} alt={`Photo ${idx + 1}`} preset={SQUARE_LG} pending={!!message.pending} compact />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {!hasImages && message.content && message.content !== '[Image]' && message.content !== '[Images]' && message.content !== '[Listing]' && (
              <div className="flex flex-wrap items-end gap-x-1.5 self-stretch">
                <p className="chat-bubble-text min-w-0 flex-1 text-[15px] leading-relaxed">{message.content}</p>
                <InlineBubbleTimestamp label={timestampLabel} isOwnMessage={!!stableIsOwnMessage} />
              </div>
            )}
            {hasImages && (!message.content || message.content === "[Image]" || message.content === "[Images]") ? (
              <div className="flex justify-end self-stretch">
                <InlineBubbleTimestamp label={timestampLabel} isOwnMessage={!!stableIsOwnMessage} />
              </div>
            ) : null}
          </ChatBubble>
          <ChatReactionPills
            reactions={reactions}
            viewerReaction={viewerReaction}
            onToggle={overlayMode ? undefined : onReactionToggle}
            className={stableIsOwnMessage ? "justify-end" : "justify-start"}
          />
        </div>
      )}

    </div>

    <ChatGalleryOverlay
      items={hasImages ? imageUrlsForDisplay.map((url, i) => ({ id: `${message.id}-img-${i}`, url, type: "photo" as const })) : []}
      isOpen={galleryOverlayOpen}
      onClose={() => setGalleryOverlayOpen(false)}
      onPhotoClick={(url, index) => {
        setGalleryOverlayOpen(false)
        handleImageClick(url, imageUrlsForDisplay, index)
      }}
    />
    <ImageGalleryModal
      images={galleryState?.images ?? []}
      isOpen={!!galleryState}
      onCloseAction={handleCloseZoom}
      initialIndex={galleryState?.initialIndex ?? 0}
    />
  </>
  )
}
